/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Message,
  Modal,
  Radio,
  Space,
  Switch,
  Typography
} from '@arco-design/web-react';
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import { get, post } from '../requestNew';
import './index.scss';

const RadioGroup = Radio.Group;
const Text = Typography.Text;

type TimeDimension = 'day' | 'week' | 'month' | 'year';

interface MemberStatisticsDailyHistoryItem {
  statDate: string;
  newMemberCount: number;
  lostMemberCount: number;
  netIncreaseCount: number;
}

interface MemberCardTypeDistributionResp {
  weekCardCount: number;
  monthCardCount: number;
  quarterCardCount: number;
  yearCardCount: number;
}

interface MemberStatisticsQueryResp {
  totalMemberCount: number;
  activeMemberCount: number;
  newMemberTotal: number;
  expiringSoonCount: number;
  dailyHistoryList: MemberStatisticsDailyHistoryItem[] | null;
  cardTypeDistribution: MemberCardTypeDistributionResp | null;
  totalMemberCountYoYRate: number | null;
  activeMemberCountYoYRate: number | null;
  newMemberTotalYoYRate: number | null;
}

interface UmsMemberCard {
  id: number;
  cardType: number;
  cardCode: string;
  cardName: string;
  cardTag: string;
  price: number;
  durationDays: number;
  dailyPrice: string;
  description: string;
  isValid: number;
  createTime?: string;
  updateTime?: string;
}

const timeDimensionOptions = [
  { label: '日', value: 'day' },
  { label: '周', value: 'week' },
  { label: '月', value: 'month' },
  { label: '年', value: 'year' }
];

const cardColorMap: Record<number, string> = {
  1: '#fffaf0',
  2: '#f7f3ff',
  3: '#eef8ff',
  4: '#edf9f2'
};

const cardPriceColorMap: Record<number, string> = {
  1: '#e9492f',
  2: '#7b3fe4',
  3: '#2f6bff',
  4: '#16a34a'
};

const cardTagColorMap: Record<number, string> = {
  1: 'linear-gradient(90deg, #ff8a6e 0%, #ff5e4d 100%)',
  2: 'linear-gradient(90deg, #8f95a3 0%, #6b7280 100%)',
  3: 'linear-gradient(90deg, #5ba8ff 0%, #2f6bff 100%)',
  4: 'linear-gradient(90deg, #42cf7a 0%, #21b45a 100%)'
};

const cardTypeLabelMap: Record<number, string> = {
  1: '新人周卡',
  2: '月卡',
  3: '季卡',
  4: '年卡'
};

const getDefaultDate = (): string => {
  const today = dayjs();
  const dayOfWeek = today.day();
  if (dayOfWeek === 1 || dayOfWeek === 2) {
    return today.subtract(dayOfWeek, 'day').format('YYYY-MM-DD');
  }
  return today.format('YYYY-MM-DD');
};

const formatYoY = (value?: number | null): string => {
  if (value === null || value === undefined) return '-';
  return `${value}%`;
};

const formatNumber = (value?: number | null): string => {
  if (value === null || value === undefined) return '0';
  return value.toLocaleString('zh-CN');
};

const getDimensionLabel = (dimension: TimeDimension): string => {
  if (dimension === 'day') return '日';
  if (dimension === 'week') return '周';
  if (dimension === 'month') return '月';
  return '年';
};

function MemberOverview() {
  const [timeDimension, setTimeDimension] = useState<TimeDimension>('week');
  const [queryDate, setQueryDate] = useState<string>(getDefaultDate());
  const [loading, setLoading] = useState(false);
  const [statisticsData, setStatisticsData] = useState<MemberStatisticsQueryResp | null>(null);

  const [memberCardLoading, setMemberCardLoading] = useState(false);
  const [memberCardList, setMemberCardList] = useState<UmsMemberCard[]>([]);

  const [editVisible, setEditVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editingCard, setEditingCard] = useState<UmsMemberCard | null>(null);
  const [editForm] = Form.useForm();

  const lineChartContainerRef = useRef<HTMLDivElement | null>(null);
  const lineChartInstanceRef = useRef<echarts.ECharts | null>(null);
  const pieChartContainerRef = useRef<HTMLDivElement | null>(null);
  const pieChartInstanceRef = useRef<echarts.ECharts | null>(null);

  const fetchStatistics = () => {
    setLoading(true);
    get<MemberStatisticsQueryResp>('/backend/memberStatistics/query', {
      dimension: timeDimension,
      queryDate
    })
      .then((res: any) => {
        if (res?.code === 200 && res?.data) {
          setStatisticsData(res.data);
        } else {
          Message.error(res?.message || '获取会员统计数据失败');
        }
      })
      .catch(() => {
        Message.error('获取会员统计数据失败');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchMemberCardList = () => {
    setMemberCardLoading(true);
    get<UmsMemberCard[]>('/memberCard/list')
      .then((res: any) => {
        if (res?.code === 200) {
          setMemberCardList(res?.data || []);
        } else {
          Message.error(res?.message || '获取会员卡配置失败');
        }
      })
      .catch(() => {
        Message.error('获取会员卡配置失败');
      })
      .finally(() => {
        setMemberCardLoading(false);
      });
  };

  useEffect(() => {
    fetchStatistics();
    fetchMemberCardList();
    return () => {
      if (lineChartInstanceRef.current) {
        lineChartInstanceRef.current.dispose();
        lineChartInstanceRef.current = null;
      }
      if (pieChartInstanceRef.current) {
        pieChartInstanceRef.current.dispose();
        pieChartInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lineSeriesData = useMemo(() => {
    const list = statisticsData?.dailyHistoryList || [];
    return {
      xData: list.map(item => item.statDate),
      newData: list.map(item => item.newMemberCount),
      lostData: list.map(item => item.lostMemberCount),
      netData: list.map(item => item.netIncreaseCount)
    };
  }, [statisticsData]);

  useEffect(() => {
    if (loading) return;
    const historyList = statisticsData?.dailyHistoryList;
    if (!historyList || historyList.length === 0) {
      if (lineChartInstanceRef.current) {
        lineChartInstanceRef.current.dispose();
        lineChartInstanceRef.current = null;
      }
      return;
    }

    const timer = window.setTimeout(() => {
      const container = lineChartContainerRef.current;
      if (!container) return;
      if (lineChartInstanceRef.current) {
        lineChartInstanceRef.current.dispose();
        lineChartInstanceRef.current = null;
      }
      const chart = echarts.init(container);
      lineChartInstanceRef.current = chart;
      chart.setOption(
        {
          tooltip: { trigger: 'axis' },
          legend: {
            data: ['新增会员', '流失会员', '净增会员'],
            top: 8
          },
          grid: {
            left: '4%',
            right: '3%',
            bottom: '8%',
            containLabel: true
          },
          xAxis: {
            type: 'category',
            data: lineSeriesData.xData
          },
          yAxis: {
            type: 'value'
          },
          series: [
            {
              name: '新增会员',
              type: 'line',
              smooth: true,
              data: lineSeriesData.newData
            },
            {
              name: '流失会员',
              type: 'line',
              smooth: true,
              data: lineSeriesData.lostData
            },
            {
              name: '净增会员',
              type: 'line',
              smooth: true,
              data: lineSeriesData.netData
            }
          ]
        },
        true
      );
      chart.resize();
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [lineSeriesData, statisticsData, loading]);

  useEffect(() => {
    if (loading) return;
    const distribution = statisticsData?.cardTypeDistribution;
    if (!distribution) {
      if (pieChartInstanceRef.current) {
        pieChartInstanceRef.current.dispose();
        pieChartInstanceRef.current = null;
      }
      return;
    }

    const timer = window.setTimeout(() => {
      const container = pieChartContainerRef.current;
      if (!container) return;
      if (pieChartInstanceRef.current) {
        pieChartInstanceRef.current.dispose();
        pieChartInstanceRef.current = null;
      }
      const chart = echarts.init(container);
      pieChartInstanceRef.current = chart;
      chart.setOption(
        {
          tooltip: { trigger: 'item' },
          legend: {
            orient: 'vertical',
            left: 8,
            top: 20
          },
          series: [
            {
              type: 'pie',
              radius: ['45%', '70%'],
              center: ['65%', '55%'],
              label: {
                formatter: '{b}'
              },
              data: [
                { name: '周卡', value: distribution.weekCardCount || 0 },
                { name: '月卡', value: distribution.monthCardCount || 0 },
                { name: '季卡', value: distribution.quarterCardCount || 0 },
                { name: '年卡', value: distribution.yearCardCount || 0 }
              ]
            }
          ]
        },
        true
      );
      chart.resize();
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [statisticsData, loading]);

  useEffect(() => {
    const handleResize = () => {
      lineChartInstanceRef.current?.resize();
      pieChartInstanceRef.current?.resize();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleQuery = () => {
    fetchStatistics();
  };

  const handleReset = () => {
    setTimeDimension('week');
    setQueryDate(getDefaultDate());
  };

  const openEditModal = (record: UmsMemberCard) => {
    setEditingCard(record);
    editForm.setFieldsValue({
      cardType: record.cardType,
      cardCode: record.cardCode,
      cardName: record.cardName,
      cardTag: record.cardTag,
      price: record.price,
      durationDays: record.durationDays,
      dailyPrice: record.dailyPrice,
      description: record.description,
      isValid: record.isValid === 1
    });
    setEditVisible(true);
  };

  const closeEditModal = () => {
    setEditVisible(false);
    setEditingCard(null);
    editForm.resetFields();
  };

  const submitEdit = () => {
    if (!editingCard?.id) return;
    editForm.validate().then(values => {
      const payload = {
        cardType: Number(values.cardType),
        cardCode: values.cardCode,
        cardName: values.cardName,
        cardTag: values.cardTag,
        price: values.price,
        durationDays: Number(values.durationDays),
        dailyPrice: values.dailyPrice,
        description: values.description,
        isValid: values.isValid ? 1 : 0
      };
      setEditLoading(true);
      post(`/memberCard/update/${editingCard.id}`, payload)
        .then((res: any) => {
          if (res?.code === 200) {
            Message.success('会员卡配置更新成功');
            closeEditModal();
            fetchMemberCardList();
          } else {
            Message.error(res?.message || '会员卡配置更新失败');
          }
        })
        .catch(() => {
          Message.error('会员卡配置更新失败');
        })
        .finally(() => {
          setEditLoading(false);
        });
    });
  };

  const renderInputByField = (field: string) => {
    if (field === 'cardType') {
      return (
        <RadioGroup
          options={[
            { label: '新人周卡', value: 1 },
            { label: '月卡', value: 2 },
            { label: '季卡', value: 3 },
            { label: '年卡', value: 4 }
          ]}
        />
      );
    }
    if (field === 'price') return <InputNumber min={0} precision={2} style={{ width: '100%' }} />;
    if (field === 'durationDays') return <InputNumber min={1} precision={0} style={{ width: '100%' }} />;
    if (field === 'isValid') return <Switch checkedText="启用" uncheckedText="停用" />;
    if (field === 'description') return <Input.TextArea placeholder="请输入会员卡描述" />;
    return <Input placeholder="请输入内容" />;
  };

  return (
    <div className="member-overview-page">
      <Card className="filter-card">
        <Space size="large" wrap align="center">
          <div className="filter-item">
            <Text>时间维度</Text>
            <RadioGroup
              options={timeDimensionOptions}
              type="button"
              value={timeDimension}
              onChange={(val: TimeDimension) => setTimeDimension(val)}
            />
          </div>
          <div className="filter-item">
            <Text>日期</Text>
            <DatePicker
              style={{ width: 200 }}
              value={queryDate ? dayjs(queryDate) : undefined}
              onChange={(v: any) => setQueryDate(v ? dayjs(v).format('YYYY-MM-DD') : '')}
            />
          </div>
          <div className="filter-item">
            <Button type="primary" loading={loading} onClick={handleQuery}>
              查询
            </Button>
            <Button type="outline" loading={loading} onClick={handleReset}>
              重置
            </Button>
          </div>
        </Space>
      </Card>

      <section className="section-block">
        <h3 className="section-title">会员卡种配置</h3>
        <div className="member-card-grid">
          {memberCardList.map(item => (
            <Card key={item.id} className="member-config-card" loading={memberCardLoading}>
              <div className="config-top" style={{ background: cardColorMap[item.cardType] || '#f7f8fa' }}>
                {item.cardTag ? (
                  <span className="card-tag" style={{ background: cardTagColorMap[item.cardType] || '#6b7280' }}>
                    {item.cardTag}
                  </span>
                ) : null}
                <div className="config-title">{item.cardName || cardTypeLabelMap[item.cardType] || '-'}</div>
                <div className="config-price" style={{ color: cardPriceColorMap[item.cardType] || '#1d2129' }}>
                  ¥{item.price}
                </div>
                <div className="config-desc">{item.description || '-'}</div>
              </div>
              <div className="config-footer">
                <span className={item.isValid === 1 ? 'status-tag enabled' : 'status-tag disabled'}>
                  {item.isValid === 1 ? '已启用' : '已停用'}
                </span>
                <Button size="small" onClick={() => openEditModal(item)}>
                  编辑
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section className="section-block">
        <h3 className="section-title">数据概览</h3>
        <div className="overview-grid">
          <Card className="overview-item">
            <div className="label">会员总人数</div>
            <div className="value">{formatNumber(statisticsData?.totalMemberCount)}</div>
            <div className="sub">同比：{timeDimension === 'day' ? formatYoY(statisticsData?.totalMemberCountYoYRate) : '-'}</div>
          </Card>
          <Card className="overview-item">
            <div className="label">有效会员数</div>
            <div className="value">{formatNumber(statisticsData?.activeMemberCount)}</div>
            <div className="sub">同比：{timeDimension === 'day' ? formatYoY(statisticsData?.activeMemberCountYoYRate) : '-'}</div>
          </Card>
          <Card className="overview-item">
            <div className="label">新增会员</div>
            <div className="value">{formatNumber(statisticsData?.newMemberTotal)}</div>
            <div className="sub">同比：{timeDimension === 'day' ? formatYoY(statisticsData?.newMemberTotalYoYRate) : '-'}</div>
          </Card>
          <Card className="overview-item">
            <div className="label">即将过期</div>
            <div className="value">{formatNumber(statisticsData?.expiringSoonCount)}</div>
            <div className="sub">时间维度：{getDimensionLabel(timeDimension)}</div>
          </Card>
        </div>
      </section>

      <div className="chart-grid">
        <Card title="会员增长趋势" loading={loading}>
          {statisticsData?.dailyHistoryList && statisticsData.dailyHistoryList.length > 0 ? (
            <div ref={lineChartContainerRef} style={{ height: 360 }} />
          ) : (
            <div className="empty-text">当前维度暂无趋势数据</div>
          )}
        </Card>
        <Card title="会员卡类型分布" loading={loading}>
          {statisticsData?.cardTypeDistribution ? (
            <div ref={pieChartContainerRef} style={{ height: 360 }} />
          ) : (
            <div className="empty-text">暂无会员卡分布数据</div>
          )}
        </Card>
      </div>

      <Modal
        title={`编辑会员卡配置${editingCard?.cardName ? ` - ${editingCard.cardName}` : ''}`}
        visible={editVisible}
        onCancel={closeEditModal}
        onOk={submitEdit}
        confirmLoading={editLoading}
        unmountOnExit
        style={{ width: 620 }}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item field="cardType" label="会员卡类型" rules={[{ required: true, message: '请选择会员卡类型' }]}>
            {renderInputByField('cardType')}
          </Form.Item>
          <Form.Item field="cardCode" label="会员编码前缀" rules={[{ required: true, message: '请输入会员编码前缀' }]}>
            {renderInputByField('cardCode')}
          </Form.Item>
          <Form.Item field="cardName" label="会员卡名称" rules={[{ required: true, message: '请输入会员卡名称' }]}>
            {renderInputByField('cardName')}
          </Form.Item>
          <Form.Item field="cardTag" label="卡种标签">
            {renderInputByField('cardTag')}
          </Form.Item>
          <Form.Item field="price" label="会员卡价格" rules={[{ required: true, message: '请输入会员卡价格' }]}>
            {renderInputByField('price')}
          </Form.Item>
          <Form.Item field="durationDays" label="有效期天数" rules={[{ required: true, message: '请输入有效期天数' }]}>
            {renderInputByField('durationDays')}
          </Form.Item>
          <Form.Item field="dailyPrice" label="日均价格">
            {renderInputByField('dailyPrice')}
          </Form.Item>
          <Form.Item field="description" label="会员卡描述">
            {renderInputByField('description')}
          </Form.Item>
          <Form.Item field="isValid" label="是否启用" triggerPropName="checked">
            {renderInputByField('isValid')}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default MemberOverview;
